<template>
	<div class="pq">
		<div class="pq-head">
			<div>
				<h5 class="mb-0">{{ title }}</h5>
				<span class="pq-sub" v-if="!loading">{{ quotes.length ? quotes.length + (quotes.length === 1 ? ' quote' : ' quotes') + ' in the Pricing Engine' : 'No quotes in the Pricing Engine yet' }}</span>
			</div>
			<router-link class="btn btn-primary btn-sm" :to="newQuoteTo"><i class="fa-regular fa-plus me-1"></i> New quote</router-link>
		</div>
		<div v-if="loading" class="text-muted small"><i class="fas fa-spinner fa-spin me-1"></i> Loading…</div>
		<div v-else-if="error" class="text-danger small">{{ error }}</div>
		<table v-else-if="quotes.length" class="table table-sm mb-0 pq-table">
			<thead>
				<tr><th>Quote</th><th>Status</th><th class="text-end">Price</th><th>Owner</th><th>Updated</th></tr>
			</thead>
			<tbody>
				<tr v-for="q in shown" :key="q.id">
					<td><router-link :to="openTo(q)">{{ q.number }}</router-link><span class="text-muted ms-1 small text-capitalize">{{ q.motion }}</span></td>
					<td><span class="badge rounded-pill" :class="toneClass(q.status)">{{ q.status }}</span></td>
					<td class="text-end text-nowrap">{{ money(q.annual_net_cents) }} a year<template v-if="q.one_time_net_cents"> + {{ money(q.one_time_net_cents) }} once</template></td>
					<td>{{ q.owner || '—' }}</td>
					<td class="text-muted text-nowrap">{{ fmtDate(q.updated_at) }}</td>
				</tr>
			</tbody>
		</table>
		<button v-if="quotes.length > limit && !all" type="button" class="btn btn-link btn-sm p-0 mt-1" @click="all = true">Show all {{ quotes.length }}</button>
	</div>
</template>

<script>
import restApi from '@/api/index'

// The Pricing Engine's quotes for one account, on the client, Accounts (Beta) and municipality pages, with the way in.
export default {
	name: 'PricingQuotesCard',
	props: {
		clientId: { type: [Number, String], default: null },
		municipalityId: { type: [Number, String], default: null },
		title: { type: String, default: 'Quotes' },
		limit: { type: Number, default: 5 },
	},
	data() {
		return { quotes: [], loading: true, error: null, all: false }
	},
	computed: {
		shown() { return this.all ? this.quotes : this.quotes.slice(0, this.limit) },
		newQuoteTo() { return this.clientId ? `/pricing?client=${this.clientId}` : `/pricing?municipality=${this.municipalityId}` },
	},
	watch: {
		clientId() { this.load() },
		municipalityId() { this.load() },
	},
	created() { this.load() },
	methods: {
		async load() {
			if (!this.clientId && !this.municipalityId) { this.quotes = []; this.loading = false; return }
			this.loading = true; this.error = null
			try {
				const params = this.clientId ? { client_id: this.clientId } : { municipality_id: this.municipalityId }
				const { data } = await restApi.get('/pricing/quotes/for', { params })
				this.quotes = data
			} catch (e) {
				this.error = 'The quotes could not be loaded.'
			} finally {
				this.loading = false
			}
		},
		openTo(q) { return `/pricing/quotes/${q.id}/` + (['draft', 'waiting_inputs'].includes(q.state) ? 'build' : 'send') },
		money(c) { return '$' + Math.round((c || 0) / 100).toLocaleString() },
		fmtDate(v) { return v ? new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—' },
		toneClass(status) {
			return { Draft: 'text-bg-secondary', 'Waiting for finance': 'text-bg-warning', 'Ready to send': 'text-bg-primary', Sent: 'text-bg-success', Won: 'text-bg-success', Lost: 'text-bg-danger', Expired: 'text-bg-danger' }[status] || 'text-bg-light text-dark'
		},
	},
}
</script>

<style scoped>
.pq-head { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 8px; }
.pq-sub { font-size: 12.5px; color: #6c757d; }
.pq-table td, .pq-table th { font-size: 13px; vertical-align: middle; }
</style>
